'use client'

import { AddIcon } from '@repo/ui/AddIcon'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { DocumentIcon } from '@repo/ui/DocumentIcon'
import { DownloadIcon } from '@repo/ui/DownloadIcon'
import { GridToolbarColumnsButton } from '@mui/x-data-grid-pro'
import { SettingsIcon } from '@repo/ui/SettingsIcon'
import { useCallback, useState } from 'react'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIStack from '@mui/material/Stack'

type ClaimsToolbarModuleProps = {
  showNewClaim?: boolean
  showManagePermissions?: boolean
  isOvertimeTable?: boolean
  onNewClaim?: () => void
  onSubmitDrafts?: () => void
  onExportCsv?: () => void
  onManagePermissions?: () => void
}

export const ClaimsToolbarModule = ({
  showNewClaim = false,
  showManagePermissions = false,
  isOvertimeTable = false,
  onNewClaim,
  onSubmitDrafts,
  onExportCsv,
  onManagePermissions
}: ClaimsToolbarModuleProps) => {
  const [mainAnchorEl, setMainAnchorEl] = useState<HTMLElement | null>(null)

  const openMainMenu = Boolean(mainAnchorEl)

  const handleMainButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMainAnchorEl(event.currentTarget)
  }, [])

  const handleCloseAll = useCallback(() => {
    setMainAnchorEl(null)
  }, [])

  const handleNewClaim = useCallback(() => {
    onNewClaim?.()
    handleCloseAll()
  }, [onNewClaim, handleCloseAll])

  const handleSubmitDrafts = useCallback(() => {
    onSubmitDrafts?.()
    handleCloseAll()
  }, [onSubmitDrafts, handleCloseAll])

  const handleExportCsv = useCallback(() => {
    onExportCsv?.()
    handleCloseAll()
  }, [onExportCsv, handleCloseAll])

  const handleManagePermissions = useCallback(() => {
    onManagePermissions?.()
    handleCloseAll()
  }, [onManagePermissions, handleCloseAll])

  return (
    <MUIStack
      display="flex"
      justifyContent="center"
      alignItems="flex-end"
      sx={{ marginRight: '1rem', position: 'relative' }}
    >
      <MUIButton
        aria-haspopup="true"
        aria-controls={openMainMenu ? 'claims-actions-menu' : undefined}
        variant="contained"
        color="primary"
        onClick={handleMainButtonClick}
        sx={{
          backgroundColor: '#F5F6F8',
          border: '1px solid rgba(5, 28, 51, 0.23)',
          color: '#051C33',
          display: 'flex',
          fontSize: '0.75rem',
          mb: '1rem',
          padding: '4px 4px 4px 12px',
          '&:hover': { backgroundColor: '#F5F6F8' }
        }}
      >
        Actions
        <ChevronRightIcon
          sx={{ transform: openMainMenu ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }}
        />
      </MUIButton>

      <MUIMenu
        id="claims-actions-menu"
        anchorEl={mainAnchorEl}
        open={openMainMenu}
        onClose={handleCloseAll}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          [`& .MuiPaper-root`]: { width: '230px', position: 'relative !important' },
          [`& .MuiMenuItem-root`]: { width: '212px' }
        }}
      >
        {showNewClaim && (
          <MUIMenuItem
            onClick={handleNewClaim}
            sx={{ cursor: 'pointer', height: 46, width: 230, mr: '8px', mb: '4px' }}
          >
            <AddIcon sx={{ height: 18, width: 18, mr: 2 }} />
            {isOvertimeTable ? 'New overtime claim' : 'New expenses claim'}
          </MUIMenuItem>
        )}

        {showNewClaim && (
          <MUIMenuItem
            onClick={handleSubmitDrafts}
            sx={{ cursor: 'pointer', height: 46, width: 230, mr: '8px', mb: '4px' }}
          >
            <DocumentIcon sx={{ height: 18, width: 18, mr: 2 }} />
            Submit drafts
          </MUIMenuItem>
        )}

        <MUIMenuItem sx={{ cursor: 'pointer', height: 46, width: 230 }}>
          <MUIBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              [`& > button`]: {
                color: 'rgba(5, 28, 51, 0.87)',
                gap: '2px',
                ml: '-3px',
                mt: '4px',
                pt: 0,
                pb: 0,
                pr: 0,
                textTransform: 'capitalize !important',
                width: '15rem',
                justifyContent: 'flex-start',
                height: '48px',
                paddingLeft: '5px'
              },
              [`& > span`]: { marginRight: '3px' },
              [`& > svg`]: { fontSize: '24px' }
            }}
          >
            <GridToolbarColumnsButton />
          </MUIBox>
        </MUIMenuItem>

        <MUIMenuItem
          onClick={handleExportCsv}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-start',
            height: 46,
            width: 230
          }}
        >
          <DownloadIcon sx={{ height: 18, width: 18, mr: 2, ml: '0' }} />
          Export as CSV
        </MUIMenuItem>

        {showManagePermissions && (
          <MUIMenuItem
            onClick={handleManagePermissions}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'flex-start',
              height: 46,
              width: 230
            }}
          >
            <SettingsIcon sx={{ height: 18, width: 18, mr: 2, ml: '0' }} />
            Manage permissions
          </MUIMenuItem>
        )}
      </MUIMenu>
    </MUIStack>
  )
}
